import React from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  IconButton,
  Chip,
  useTheme,
  useMediaQuery,
  Stack,
  Avatar,
  Divider,
  Hidden,
  alpha,
  Menu,
  MenuItem,
  ListItemIcon
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Business,
  Person,
  Logout as LogoutIcon,
  LockReset as LockResetIcon
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { useSidebar } from '../../../contexts/SidebarContext';
import { HeaderAction, LayoutConfig } from '../types';
import HeaderNotifications from '../Header/Notifications';
import { useTabNavigation } from '../../../hooks/useTabNavigation';
import GenericDialog from 'src/components/GenericDialog';
import GenericInput from 'src/components/input-components/GenericTextInput';
import { useDispatch } from 'react-redux';
import { LogoutUser } from 'src/redux/user/userReducer';

interface HeaderProps {
  title?: string;
  actions?: HeaderAction[];
  config?: LayoutConfig;
  user?: {
    name: string;
    email: string;
    avatar?: string;
    role: string;
  };
}

const StyledAppBar = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'sidebarWidth' && prop !== 'isMobile'
})<{ sidebarWidth: number; isMobile: boolean }>(({ theme, sidebarWidth, isMobile }) => ({
  zIndex: theme.zIndex.drawer + 1,
  backgroundColor: alpha(theme.palette.background.paper, 0.95),
  backdropFilter: 'blur(12px)',
  boxShadow: 'none',
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.standard,
  }),
  ...(!isMobile && {
    marginLeft: sidebarWidth,
    width: `calc(100% - ${sidebarWidth}px)`,
  }),
  ...(isMobile && {
    width: '100%',
    marginLeft: 0,
  }),
  color: theme.palette.text.primary
}));

const UserSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginRight: theme.spacing(2),
  paddingLeft: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: 'none',
  padding: `${theme.spacing(0.5)} ${theme.spacing(1)}`,
  transition: 'background 0.2s',
  '&:hover': {
    background: theme.palette.action.hover,
  },
  [theme.breakpoints.down('md')]: {
    gap: theme.spacing(1),
    padding: `${theme.spacing(0.5)} ${theme.spacing(1.5)}`,
  },
  [theme.breakpoints.down('sm')]: {
    '& .user-text': {
      display: 'none'
    }
  }
}));

const TitleSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  [theme.breakpoints.down('md')]: {
    gap: theme.spacing(1),
  },
  [theme.breakpoints.down('sm')]: {
    '& .title-icon': {
      display: 'none'
    }
  }
}));

export default function Header({
  title,
  actions = [],
  config = {
    sidebarWidth: {
      collapsed: 0,
      expanded: 0
    },
    headerHeight: 0,
    showTabs: true,
    showUserInfo: false,
    allowSidebarToggle: false
  },
  user
}: HeaderProps) {
  const theme = useTheme();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const upMd = useMediaQuery(theme.breakpoints.up('md'));
  const upLg = useMediaQuery(theme.breakpoints.up('lg'));
  const upXl = useMediaQuery(theme.breakpoints.up('xl'));

  const { collapsed, getWidth } = useSidebar();
  const dispatch = useDispatch();

  // Responsive sidebar width logic (same as Sidebar)
  const expandedWidth = React.useMemo(() => {
    if (isMobile) return getWidth(); // keep config width for mobile drawer
    if (upXl) return 320;
    if (upLg) return 280;
    if (upMd) return 240;
    return 220; // small laptops
  }, [isMobile, upMd, upLg, upXl, getWidth]);

  const collapsedWidth = config.sidebarWidth.collapsed || 72;
  const isCollapsed = collapsed && !isMobile;
  const sidebarWidth = isCollapsed ? collapsedWidth : expandedWidth;

  const showTabs = config.showTabs !== false;
  const showUserInfo = config.showUserInfo !== false;

  // Change password dialog state
  const [changePwdOpen, setChangePwdOpen] = React.useState(false);
  const [oldPassword, setOldPassword] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [errors, setErrors] = React.useState<{oldPassword?: string; newPassword?: string}>({});

  // Initialize tab navigation
  useTabNavigation();

  const [userMenuAnchor, setUserMenuAnchor] = React.useState<null | HTMLElement>(null);
  const openUserMenu = Boolean(userMenuAnchor);
  const handleUserBoxClick = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };
  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };
  const handleLogout = () => {
    dispatch(LogoutUser());
    handleUserMenuClose();
  };
  const handleUpdatePassword = () => {
    setChangePwdOpen(true);
    handleUserMenuClose();
  };
  const handleChangePassword = async () => {
    setLoading(true);
    setErrors({});
    setTimeout(() => {
      setLoading(false);
      setChangePwdOpen(false);
      setOldPassword('');
      setNewPassword('');
    }, 1200);
  };

  const visibleActions = actions.filter((action) => action.visible !== false);

  return (
    <StyledAppBar position="fixed" elevation={0} sidebarWidth={sidebarWidth} isMobile={isMobile}>
      <Toolbar sx={{ minHeight: config.headerHeight || 64, px: { xs: 2, sm: 3 }, gap: { xs: 1, sm: 2 } }}>
        {title && (
          <TitleSection>
            <Business className="title-icon" color="primary" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
            <Typography
              variant={isMobile ? 'h6' : 'h5'}
              component="h1"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: { xs: 150, sm: 250, md: 400 }
              }}
            >
              {title}
            </Typography>
          </TitleSection>
        )}

        <Box sx={{ flex: 1 }} />

        <Stack direction="row" spacing={{ xs: 0.5, sm: 1 }} alignItems="center">
          {visibleActions.map((action, i) => (
            <Hidden key={action.id} smDown={i > 1}>
              <IconButton
                onClick={action.onClick}
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  color: 'text.primary',
                  '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) }
                }}
                title={action.tooltip || action.label}
              >
                {action.badge ? (
                  <Box position="relative">
                    {action.icon}
                    <Chip
                      label={action.badge}
                      size="small"
                      color="error"
                      sx={{ position: 'absolute', top: -8, right: -8, height: 16, minWidth: 16, fontSize: '0.65rem' }}
                    />
                  </Box>
                ) : (
                  action.icon
                )}
              </IconButton>
            </Hidden>
          ))}

          <Box sx={{ display: 'flex', alignItems: 'center', borderRadius: 0, pl: 0, py: 0.5, ml: 2 }}>
            <HeaderNotifications />
            {showUserInfo && user && (
              <>
                <Hidden smDown>
                  <Divider orientation="vertical" flexItem sx={{ mx: 1, height: 32 }} />
                </Hidden>
                <UserSection onClick={handleUserBoxClick} sx={{ cursor: 'pointer' }}>
                  <Avatar src={user.avatar} sx={{ width: { xs: 32, sm: 36 }, height: { xs: 32, sm: 36 }, bgcolor: 'primary.main' }}>
                    {user.name?.charAt(0) || <Person />}
                  </Avatar>
                  <Box className="user-text" sx={{ minWidth: 0}}>
                    <Typography 
                      variant="subtitle1"
                      fontWeight={700}
                      noWrap 
                      sx={{ color: 'text.primary', fontSize: { xs: '1rem', sm: '1.1rem' } }}>
                      {user.name}
                    </Typography>
                    <Typography 
                      color="text.secondary" 
                      noWrap 
                      sx={{ lineHeight: 1, fontSize: { xs: '0.7rem', sm: '0.8rem' }, fontWeight: 400 }}>
                      {user.role}
                    </Typography>
                  </Box>
                </UserSection>
                <Menu
                  anchorEl={userMenuAnchor}
                  open={openUserMenu}
                  onClose={handleUserMenuClose}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <MenuItem onClick={handleUpdatePassword}>
                    <ListItemIcon><LockResetIcon fontSize="small" /></ListItemIcon>
                    Update Password
                  </MenuItem>
                  <MenuItem onClick={handleLogout}>
                    <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                    Logout
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </Stack>
      </Toolbar>
      <GenericDialog open={changePwdOpen} title="Change Password" onClose={() => setChangePwdOpen(false)}>
        <GenericInput
          title="Old Password"
          type="password"
          value={oldPassword}
          onChange={setOldPassword}
          name="oldPassword"
          error={errors.oldPassword}
          disabled={loading}
        />
        <GenericInput
          title="New Password"
          type="password"
          value={newPassword}
          onChange={setNewPassword}
          name="newPassword"
          error={errors.newPassword}
          disabled={loading}
        />
        <Box mt={2} display="flex" justifyContent="flex-end" gap={2}>
          <button onClick={() => setChangePwdOpen(false)} disabled={loading}>Cancel</button>
          <button style={{background:'#2563eb',color:'#fff',border:'none',padding:'8px 16px',borderRadius:4}} onClick={handleChangePassword} disabled={loading}>
            {loading ? 'Updating...' : 'Update Password'}
          </button>
        </Box>
      </GenericDialog>
    </StyledAppBar>
  );
}
